import React, { useState, useEffect } from 'react';
import { apiService } from '../../service/Api';

const CompanySelector = ({ selectedCompany, onSelectCompany, onCreateCompany }) => {
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [showEditIntelligence, setShowEditIntelligence] = useState(false);
  const [editingCompany, setEditingCompany] = useState(null);
  const [intelligenceJson, setIntelligenceJson] = useState('');
  const [createFormData, setCreateFormData] = useState({
    name: '',
    website: ''
  });

  useEffect(() => {
    loadCompanies();
  }, []);

  const loadCompanies = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await apiService.getAllCompanies();
      if (response.data.success) {
        setCompanies(response.data.data || []);
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load companies');
      console.error('Error loading companies:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateCompany = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const companyData = {
        name: createFormData.name,
        domain: createFormData.website
      };

      const response = await apiService.createCompany(companyData);
      if (response.data.success) {
        const newCompany = response.data.data;
        setCompanies([newCompany, ...companies]);
        setCreateFormData({ name: '', website: '' });
        setShowCreateForm(false);
        // Show intelligence editor after creation
        setEditingCompany(newCompany);
        setIntelligenceJson(JSON.stringify(newCompany.intelligence_json || {}, null, 2));
        setShowEditIntelligence(true);
        onSelectCompany && onSelectCompany(newCompany);
        onCreateCompany && onCreateCompany(newCompany);
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to create company');
      console.error('Error creating company:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveIntelligence = async () => {
    if (!editingCompany) return;
    
    setLoading(true);
    setError(null);

    try {
      const parsedJson = JSON.parse(intelligenceJson);
      const response = await apiService.updateCompanyIntelligence(editingCompany.id, parsedJson);
      if (response.data.success) {
        const updatedCompany = response.data.data;
        setCompanies(companies.map(c => c.id === updatedCompany.id ? updatedCompany : c));
        setEditingCompany(updatedCompany);
        if (selectedCompany?.id === updatedCompany.id) {
          onSelectCompany && onSelectCompany(updatedCompany);
        }
        setShowEditIntelligence(false);
        setEditingCompany(null);
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update intelligence. Please check JSON format.');
      console.error('Error updating intelligence:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleEditIntelligence = (company) => {
    setEditingCompany(company);
    setIntelligenceJson(JSON.stringify(company.intelligence_json || {}, null, 2));
    setShowEditIntelligence(true);
  };


  return (
    <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-gray-900">Company</h3>
        <button
          type="button"
          onClick={() => setShowCreateForm(!showCreateForm)}
          className="text-sm px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
        >
          {showCreateForm ? 'Cancel' : '+ New Company'}
        </button>
      </div>

      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md text-red-700 text-sm">
          {error}
        </div>
      )}

      {showCreateForm ? (
        <form onSubmit={handleCreateCompany} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Company Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={createFormData.name}
              onChange={(e) => setCreateFormData({ ...createFormData, name: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Website <span className="text-red-500">*</span>
            </label>
            <input
              type="url"
              value={createFormData.website}
              onChange={(e) => setCreateFormData({ ...createFormData, website: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="https://www.example.com"
              required
            />
          </div>

          <div className="flex gap-3">
            <button
              type="submit"
              disabled={loading}
              className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-gray-400 transition-colors"
            >
              {loading ? 'Creating...' : 'Create Company'}
            </button>
            <button
              type="button"
              onClick={() => setShowCreateForm(false)}
              className="px-4 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 transition-colors"
            >
              Cancel
            </button>
          </div>
        </form>
      ) : (
        <div>
          {loading && companies.length === 0 ? (
            <div className="text-center py-8 text-gray-500">Loading companies...</div>
          ) : companies.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              No companies found. Create one to get started.
            </div>
          ) : (
            <select
              value={selectedCompany?.id || ''}
              onChange={(e) => {
                const company = companies.find(c => c.id === parseInt(e.target.value));
                onSelectCompany && onSelectCompany(company);
              }}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">Select a company...</option>
              {companies.map((company) => (
                <option key={company.id} value={company.id}>
                  {company.name || company.company_name}
                </option>
              ))}
            </select>
          )}

          {selectedCompany && (
            <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-md">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <p className="font-medium text-gray-900">{selectedCompany.name || selectedCompany.company_name}</p>
                  {selectedCompany.domain && (
                    <p className="text-sm text-gray-600 mt-1">{selectedCompany.domain}</p>
                  )}
                  <button
                    type="button"
                    onClick={() => handleEditIntelligence(selectedCompany)}
                    className="mt-2 text-sm text-blue-600 hover:text-blue-800 underline"
                  >
                    Edit Intelligence JSON
                  </button>
                </div>
                <button
                  type="button"
                  onClick={() => onSelectCompany && onSelectCompany(null)}
                  className="text-gray-400 hover:text-gray-600 ml-2"
                >
                  ✕
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {showEditIntelligence && editingCompany && (
        <div className="mt-6 p-6 bg-gray-50 border border-gray-200 rounded-lg">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900">
              Edit Intelligence JSON - {editingCompany.name}
            </h3>
            <button
              type="button"
              onClick={() => {
                setShowEditIntelligence(false);
                setEditingCompany(null);
                setIntelligenceJson('');
              }}
              className="text-gray-400 hover:text-gray-600"
            >
              ✕
            </button>
          </div>

          <textarea
            value={intelligenceJson}
            onChange={(e) => setIntelligenceJson(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 font-mono text-sm"
            rows="20"
            placeholder='{"key": "value"}'
          />

          <div className="flex gap-3 mt-4">
            <button
              type="button"
              onClick={handleSaveIntelligence}
              disabled={loading}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-gray-400 transition-colors"
            >
              {loading ? 'Saving...' : 'Save Intelligence'}
            </button>
            <button
              type="button"
              onClick={() => {
                setShowEditIntelligence(false);
                setEditingCompany(null);
                setIntelligenceJson('');
              }}
              className="px-4 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 transition-colors"
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default CompanySelector;
